import { createContext, useCallback, useContext, useRef, useState } from 'react';

/** App-wide toasts: transient outcome feedback (success/error/info). */

interface Toast {
  id: number;
  kind: 'ok' | 'danger' | 'info';
  text: string;
}

interface ToastApi {
  success: (text: string) => void;
  error: (text: string) => void;
  info: (text: string) => void;
}

const ToastContext = createContext<ToastApi>({
  success: () => undefined,
  error: () => undefined,
  info: () => undefined,
});

export function useToast(): ToastApi {
  return useContext(ToastContext);
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const push = useCallback((kind: Toast['kind'], text: string) => {
    const id = nextId.current++;
    setToasts((t) => [...t.slice(-3), { id, kind, text }]);
    const ttl = kind === 'danger' ? 8000 : 5000;
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), ttl);
  }, []);

  const api = useRef<ToastApi>({
    success: (text) => push('ok', text),
    error: (text) => push('danger', text),
    info: (text) => push('info', text),
  });
  api.current.success = (text) => push('ok', text);
  api.current.error = (text) => push('danger', text);
  api.current.info = (text) => push('info', text);

  return (
    <ToastContext.Provider value={api.current}>
      {children}
      <div className="toast-stack" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast toast--${t.kind}`} role={t.kind === 'danger' ? 'alert' : 'status'}>
            <span style={{ flex: 1 }}>{t.text}</span>
            <button
              type="button"
              aria-label="Dismiss"
              onClick={() => setToasts((all) => all.filter((x) => x.id !== t.id))}
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
